























































































































































































































































































import {
  Form,
  FormItem,
  Input,
  Button,
  Select,
  Option,
  Upload,
} from 'element-ui'
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'

import Checkbox from '@/components/Checkbox.vue'
import ReviewPreview from '@/components/ReviewPreview.vue'

import type { ReviewSubmitPayload } from '@/models/review'

interface ImageFile {
  file: File
  url: string
}

function trimmedRequiredRule(message: string) {
  return {
    trigger: 'change',
    validator: (
      _rule: unknown,
      value: unknown,
      callback: (error?: Error) => void,
    ) => {
      if (value == null || String(value).trim() === '') {
        callback(new Error(message))
      } else {
        callback()
      }
    },
  }
}

@Component({
  components: {
    [Input.name]: Input,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [Button.name]: Button,
    [Select.name]: Select,
    [Option.name]: Option,
    [Upload.name]: Upload,
    Checkbox,
    ReviewPreview,
  },
})
export default class ReviewForm extends Vue {
  @Ref() readonly reviewForm!: Form
  @Prop({ default: '' }) initialName!: string
  @Prop({ required: true }) submitReviewAction!: (
    payload: ReviewSubmitPayload,
  ) => Promise<void>

  ratingHovered = 0
  submitValidationLock = false
  imageFiles: ImageFile[] = []
  showImageUsageDetails = false
  readonly maxReviewImages = 5

  form = {
    rating: 0,
    title: '',
    body: '',
    groupSize: '',
    displayName: '',
    allowedToPublishName: true,
    agreedToImageUsageRights: false,
  }

  rules = {
    displayName: [trimmedRequiredRule('Please enter your first name')],
    title: [trimmedRequiredRule('Please provide a review title')],
    body: [trimmedRequiredRule('Please fill in your review')],
    groupSize: [{ required: true, message: 'Please select an option' }],
    agreedToImageUsageRights: [
      {
        trigger: 'change',
        validator: (
          _rule: unknown,
          value: boolean,
          callback: (error?: Error) => void,
        ) => {
          if (!this.imageFiles.length || value === true) {
            callback()
          } else {
            callback(
              new Error(
                'Please agree to the image usage terms and conditions.',
              ),
            )
          }
        },
      },
    ],
  }

  mounted() {
    if (this.initialName) {
      this.form.displayName = this.initialName
    }
  }

  get bodyLabel(): string {
    return 'How was your holiday?'
  }

  get imageRightsGranteeName(): string {
    return this.isIslandHopping
      ? 'Sail Croatia Adventures Ltd (T/A Island Hopping)'
      : 'Sail Croatia Adventures Ltd'
  }

  get displayRating(): number {
    return this.ratingHovered > this.form.rating
      ? this.ratingHovered
      : this.form.rating
  }

  get charsRemaining(): number {
    return 1000 - this.form.body.length
  }

  get previewImages(): { url: string }[] {
    return this.imageFiles.map((img) => {
      return { url: img.url }
    })
  }

  get submitBusy(): boolean {
    return (
      this.$store.state.reviews.reviewSubmitting || this.submitValidationLock
    )
  }

  setRating(rating: number) {
    this.form = {
      ...this.form,
      rating,
    }
  }

  onPublishNameChange(checked: boolean) {
    this.form.allowedToPublishName = !checked
  }

  onImageUsageRightsChange(checked: boolean) {
    this.form.agreedToImageUsageRights = checked
    this.$nextTick(() => {
      this.reviewForm.validateField('agreedToImageUsageRights')
    })
  }

  handleBeforeUpload(file: File): boolean {
    if (this.imageFiles.length >= this.maxReviewImages) {
      return false
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/webp']
    if (!validTypes.includes(file.type)) {
      return false
    }

    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      return false
    }

    const imageFile: ImageFile = {
      file,
      url: URL.createObjectURL(file),
    }
    this.imageFiles.push(imageFile)

    return false
  }

  removeImage(index: number) {
    const removed = this.imageFiles.splice(index, 1)
    if (removed.length) {
      URL.revokeObjectURL(removed[0].url)
    }
    if (!this.imageFiles.length) {
      this.form.agreedToImageUsageRights = false
      this.showImageUsageDetails = false
      this.$nextTick(() => {
        const form = this.reviewForm as any
        form.clearValidate(['agreedToImageUsageRights'])
      })
    }
  }

  getNormalizedSubmitPayload() {
    const images = this.imageFiles.map((img) => img.file)
    return {
      ...this.form,
      displayName: this.form.displayName.trim(),
      title: this.form.title.trim(),
      body: this.form.body.trim(),
      images,
    }
  }

  scrollToFirstValidationError(): void {
    const el = this.$el.querySelector('.el-form-item.is-error')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  async submitReview(): Promise<boolean> {
    if (this.submitBusy) {
      return false
    }
    this.submitValidationLock = true
    try {
      try {
        await this.reviewForm.validate()
      } catch {
        this.$emit('validation-error')
        return false
      }
      await this.submitReviewAction(this.getNormalizedSubmitPayload())
      return true
    } catch (e) {
      return false
    } finally {
      this.submitValidationLock = false
    }
  }
}
